import React, { Suspense, useEffect, useMemo } from "react";
import { RouterProvider } from "react-router-dom";
import { router } from "./pages";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import "react-toastify/dist/ReactToastify.css";
import { Slide, ToastContainer } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ConfigProvider, theme } from "antd";
import { StyleProvider } from "@ant-design/cssinjs";
import { CacheProvider } from "@emotion/react";
import { useDispatch } from "react-redux";
import { AnimatePresence } from "framer-motion";
import * as Sentry from "@sentry/react";
import { useJsApiLoader } from "@react-google-maps/api";

// Modals & Components
import Shoeing from "./components/modals/Shoeing";
import Rider from "./components/modals/Rider";
import Track from "./components/modals/Track";
import TrackName from "./components/modals/TrackName";
import Loading from "./components/global/Loading";
import EditTrainingModal from "./components/modals/EditTrainingModal";
import AddHorseModal from "./components/modals/AddHorseModal";
import ComparaisonModal from "./components/modals/ComparaisonModal";
import EditTrainingsModal from "./components/modals/EditTrainingsModal";
import TrainingTypeModal from "./components/modals/TrainingTypeModal";
import AddVetModal from "./components/modals/AddVetModal";
import PublicTracksModal from "./components/modals/PublicTracksModal";
import EditHorseWeight from "./components/modals/EditHorseWeight";
import EditHorseHeight from "./components/modals/EditHorseHeight";
import EditHorseHrModal from "./components/modals/EditHorseHrModal";
import EditEqmDisplayNameModal from "./components/modals/EditEqmDisplayNameModal";
import EqmSubscribeModal from "./components/modals/EqmSubscribeModal";
import AddPaymentMethodeModal from "./components/modals/AddPaymentMethodeModal";

// Hooks & Contexts
import { useLocales } from "./hooks/useLocales";
import { updateMapState } from "./store/reducers/display.reducer";
import { FirstLoginProvider } from "./context/firstLoginContext";
import palette from "./theme/palette";
import { ltrCache, rtlCache } from "./theme/rtl-cache";
import Theme from "./theme";

const RTL_LANGS = ["ar"];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 2,
    },
  },
});

function App() {
  const { getTimePickerLocales, currentLang, getAntTimePickerLocales } =
    useLocales();
  const dispatch = useDispatch();
  const isRtl = RTL_LANGS.includes(currentLang);
  const isPdfPage = window.location.pathname.startsWith("/pdf");

  // Load Google Maps
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY ?? "",
    libraries: ["places", "maps", "marker", "geometry"],
  });

  useEffect(() => {
    dispatch(updateMapState(isLoaded));
  }, [dispatch, isLoaded]);

  useEffect(() => {
    document.documentElement.dir = isRtl ? "rtl" : "ltr";
    document.documentElement.lang = currentLang;
  }, [isRtl, currentLang]);

  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider
            localeText={getTimePickerLocales()}
            adapterLocale={currentLang}
            dateAdapter={AdapterDayjs}
          >
            <CacheProvider value={isRtl ? rtlCache : ltrCache}>
              <ThemeProvider theme={Theme}>
                <CssBaseline />
                <ConfigProvider
                  locale={getAntTimePickerLocales()}
                  theme={{
                    algorithm: isPdfPage
                      ? theme.defaultAlgorithm
                      : theme.darkAlgorithm,
                    token: {
                      colorPrimary: palette.dark.primary.main,
                    },
                  }}
                >
                  <Suspense fallback={<Loading></Loading>}>
                    <FirstLoginProvider>
                      <AnimatePresence mode="wait">
                        <RouterProvider router={router} />
                      </AnimatePresence>
                    </FirstLoginProvider>
                  </Suspense>
                </ConfigProvider>
                {/* enable react query devtools in dev env only */}
                {process.env.NODE_ENV === "development" && !isPdfPage && (
                  <ReactQueryDevtools initialIsOpen={false} />
                )}
                {/* Modals */}
                <Shoeing />
                <Rider />
                <Track />
                <TrackName />
                <EditTrainingModal />
                <EditTrainingsModal />
                <AddHorseModal />
                <ComparaisonModal />
                <TrainingTypeModal />
                <AddVetModal />
                <PublicTracksModal />
                <EditHorseWeight />
                <EditHorseHeight />
                <EditHorseHrModal />
                <EditEqmDisplayNameModal />
                <EqmSubscribeModal />
                <AddPaymentMethodeModal />
                {/* end Modals */}
              </ThemeProvider>
            </CacheProvider>
          </LocalizationProvider>
        </QueryClientProvider>

        {/* Prevent Toast on pdfs */}
        {!isPdfPage && (
          <ToastContainer
            position="top-right"
            pauseOnHover={false}
            closeOnClick={true}
            transition={Slide}
            autoClose={2000}
            theme="colored"
            pauseOnFocusLoss={false}
          ></ToastContainer>
        )}
      </>
    </Sentry.ErrorBoundary>
  );
}

export default App;
